import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import type { RegionCount } from './heat-map-google'
import { projectRegions, useRegionOutlines } from './heat-map-geo'
import { shadeStep } from './heat-map-scale'

/**
 * The heat map as region outlines with no map service needed (WPS-232 #6):
 * the country's regions (Natural Earth) drawn as SVG and shaded by count, any
 * country. Used whenever Google Maps is not configured, so every environment
 * gets a proper map. Hover shows the name and count; click opens the records.
 */

const FILL = [
  'fill-[color-mix(in_srgb,var(--green)_14%,var(--bg-card))]',
  'fill-[color-mix(in_srgb,var(--green)_32%,var(--bg-card))]',
  'fill-[color-mix(in_srgb,var(--green)_52%,var(--bg-card))]',
  'fill-[color-mix(in_srgb,var(--green)_75%,var(--bg-card))]',
  'fill-[var(--green)]',
]

export function OutlineHeatMap({
  country,
  countryName,
  word,
  counts,
  max,
  unit,
  onOpen,
}: {
  country: string
  countryName: string
  /** What the country calls its regions ("state", "province", "county"). */
  word: string
  counts: Map<string, RegionCount>
  max: number
  unit: string
  onOpen: (code: string) => void
}) {
  const { t } = useTranslation()
  const { data: outlines, isPending, isError } = useRegionOutlines(country)
  const map = useMemo(() => (outlines ? projectRegions(outlines.features) : null), [outlines])
  const [hover, setHover] = useState<string | null>(null)
  const hovered = hover ? counts.get(hover) : undefined

  if (isPending || isError || !map) {
    return (
      <p className="py-10 text-center text-[13px] text-[var(--ink-3)]">
        {isPending
          ? t('common.loading', 'Loading...')
          : t('dashboard.heatMap.noOutlines', 'No map for this country yet - the list shows its counts.')}
      </p>
    )
  }

  return (
    <div className="relative" data-testid="heat-map-outline" data-country={country}>
      {/* Above the map, never on it: nothing is covered. */}
      <div className="mb-1 min-h-[20px] text-[12.5px] text-[var(--ink-2)]" aria-live="polite">
        {hovered
          ? `${hovered.label}: ${hovered.value.toLocaleString()} ${unit}`
          : t('dashboard.heatMap.hoverHint', {
              defaultValue: 'Hover a {{word}} to see its count - click to open the records',
              word,
            })}
      </div>
      <svg
        viewBox={`0 0 ${map.width} ${map.height}`}
        className="mx-auto block h-auto w-full max-w-[720px]"
        role="img"
        aria-label={t('dashboard.heatMap.mapLabel', {
          defaultValue: 'Map of {{country}} shaded by count',
          country: countryName,
        })}
      >
        {map.insets.map((b, i) => (
          <rect
            key={`inset-${i}`}
            x={b.x}
            y={b.y}
            width={b.w}
            height={b.h}
            rx={6}
            className="fill-none stroke-[var(--rule)] stroke-[0.8]"
          />
        ))}
        {map.regions.map((r) => {
          const value = counts.get(r.code)?.value ?? 0
          const step = shadeStep(value, max)
          return (
            <path
              key={r.code}
              d={r.d}
              data-region={r.code}
              aria-label={`${r.name}: ${value}`}
              onMouseEnter={() => setHover(r.code)}
              onMouseLeave={() => setHover(null)}
              onClick={() => value > 0 && onOpen(r.code)}
              className={cn(
                'stroke-white stroke-[0.8] transition-[stroke-width] hover:stroke-[var(--ink)] hover:stroke-[1.6]',
                step < 0 ? 'fill-[var(--bg-subtle)]' : cn(FILL[step], 'cursor-pointer'),
              )}
            />
          )
        })}
        {/* The number on every region that has records. */}
        {map.regions.map((r) => {
          const value = counts.get(r.code)?.value ?? 0
          if (!value) return null
          const dark = shadeStep(value, max) >= 3
          return (
            <text
              key={`label-${r.code}`}
              x={r.centre[0]}
              y={r.centre[1] + 4}
              textAnchor="middle"
              className={cn(
                'pointer-events-none text-[12px] font-semibold tabular-nums',
                dark ? 'fill-white' : 'fill-[var(--ink)]',
              )}
            >
              {value.toLocaleString()}
            </text>
          )
        })}
      </svg>
    </div>
  )
}
